import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { playStoreUrl, trackEvent } from './analytics';
import Seo from './Seo';
import './App.css';
import Footer from './Footer';
import Navbar from './Navbar';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://baatcheetapp.in/#organization',
      name: 'Baatcheet',
      legalName: 'DevEffica Pvt. Ltd.',
      url: 'https://baatcheetapp.in',
      logo: 'https://baatcheetapp.in/logo.png',
      description: "India's desi adda for voice — audio calls, live rooms and voice notes.",
      sameAs: ['https://www.instagram.com/____baatcheet____/'],
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'baatcheet.connect@gmail.com',
        contactType: 'customer support',
        areaServed: 'IN',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://baatcheetapp.in/#website',
      name: 'Baatcheet',
      url: 'https://baatcheetapp.in',
      publisher: { '@id': 'https://baatcheetapp.in/#organization' },
      inLanguage: 'en-IN',
    },
  ],
};

function Home() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Baatcheet — Voice Chat App for India | Audio Calls, Live Rooms & Voice Notes"
        description="Baatcheet is India's desi adda for voice — audio calls, live rooms and voice notes. Real people, anonymous-first, free to start. Download free on Android."
        path="/"
        jsonLd={homeJsonLd}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      {/* ── Header ── */}
      <Navbar />

      <main className="shell">

        {/* ── Hero ── */}
        <section className="hero">
          <div className="hero-grid">

            {/* Left copy */}
            <div className="reveal">
              <span className="eyebrow"><span className="pulse" /> Made in Bharat · 1.2M+ vibing</span>
              <h1 className="title">
                <span className="script">Suno,</span>{' '}<span className="hi">बात</span><br />
                <span className="stamp">karein?</span>
              </h1>
              <p className="hero-sub">
                Baatcheet is a loud, colourful, scrappy <strong>desi adda</strong> for voice.
                Audio calls, live rooms, voice notes — clip karke saath rakho, jab dil chahe vibe karo.
              </p>
              <div className="hero-cta">
                <a className="btn btn-cyan" href="#cta">▶ Start your first call</a>
                <Link className="btn btn-ghost" to="/how-it-works">How it works</Link>
              </div>
              <div className="handnote">
                <span className="arrow">↙</span> 1st call free, hamesha
                <span className="sticker"><span className="star">★</span> 4.8</span>
              </div>
            </div>

            {/* Right: 3D character stage */}
            <div className="stage reveal">
              {/* Ambient glows */}
              <div className="ch1-glow" />
              <div className="ch2-glow" />
              <div className="ch3-glow" />

              {/* 3 floating 3D characters */}
              <div className="ch-wrap ch1">
                <img src="/char-girl-pink.webp" alt="Riya, a Baatcheet user" width="361" height="1402" fetchpriority="high" />
              </div>
              <div className="ch-wrap ch2">
                <img src="/char-boy.webp" alt="Aarav, a Baatcheet user" width="364" height="1402" loading="lazy" decoding="async" />
              </div>
              <div className="ch-wrap ch3">
                <img src="/char-girl-black.webp" alt="Dost, a Baatcheet user" width="381" height="1402" loading="lazy" decoding="async" />
              </div>

              {/* Sticky notes */}
              <div className="note n1">tea ready ☕<br />call lo na?</div>
              <div className="note n2">yeh app ghar<br />jaisa lagta hai 🏠</div>

              {/* Speech bubbles */}
              <div className="cmc b1">
                <div className="av a2" />
                <div><span className="nm">Riya · live</span>kuch sun lo aaj 🎙️</div>
              </div>
              <div className="cmc b2">
                <div className="av a3" />
                <div><span className="nm">Zoya</span>2am hai, neend nahi aa rahi</div>
              </div>
            </div>
          </div>

          {/* Marquee strip */}
          <div className="stripe">
            <div className="stripe-track">
              <span>दिल khol ke baat</span><span>Made in Bharat</span>
              <span>Voice journaling</span><span>Late-night vibes</span>
              <span>Real talk · real people</span><span>1.2M+ users</span>
              <span>दिल khol ke baat</span><span>Made in Bharat</span>
              <span>Voice journaling</span><span>Late-night vibes</span>
              <span>Real talk · real people</span><span>1.2M+ users</span>
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="block" id="feat">
          <div className="feat-intro reveal">
            <div>
              <div className="kicker"><span className="kicker-line" /> What's in the box</div>
              <h2 className="h">Sab masala, <span className="stamp">ek app</span>.</h2>
              <p className="lead">A complete voice-first kit for India's Gen-Z — moods, rooms, voice notes, anonymous calls, and a hustle if you've got the gift of gab.</p>
            </div>
            <div className="feat-photo">
              <img src="/group-call.webp" alt="Baatcheet squad in a group call" width="1402" height="1122" loading="lazy" decoding="async" />
              <div className="feat-photo-tag">
                <span className="cf-dot" /> Baatcheet Squad · Live
              </div>
            </div>
          </div>

          <div className="collage">

            {/* Big card: Mood calls with animated mock UI */}
            <div className="cd big reveal">
              <span className="pin" />
              <div className="ic" style={{ background: 'var(--cyan)' }}>🎧</div>
              <h3>Mood-matched audio calls</h3>
              <p>Pick a vibe — chill, <Link to="/stress-relief">vent</Link>, flirt, hustle — pair instantly with someone on the same wavelength. Anonymous till you both want more.</p>

              {/* Live call mock widget with waveform */}
              <div className="mock">
                <div className="row">
                  <div className="av a2" />
                  <div style={{ flex: 1 }}>
                    <div className="nm">Riya</div>
                    <div className="st">● live · 02:14</div>
                  </div>
                  <span className="badge">vent</span>
                </div>
                <div className="row">
                  <div className="wave">
                    <i /><i /><i /><i /><i />
                    <i /><i /><i /><i /><i />
                  </div>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="badge" style={{ background: 'var(--cyan)' }}>हिंदी</span>
                  <span className="badge" style={{ background: 'var(--rose)', color: '#fff' }}>tap to reveal</span>
                </div>
              </div>
            </div>

            {/* Live rooms card with active rooms list */}
            <div className="cd s6 cyan live-card reveal" style={{ gridColumn: 'span 6' }}>
              <span className="pin" style={{ background: 'var(--gold)' }} />
              <div className="ic">🎙️</div>
              <h3>Live rooms (mehfils)</h3>
              <p>Themed audio rooms — late-night confessions, music jams, cricket gossip, shaayri exchange.</p>

              <div className="rooms-list">
                <div className="room-row">
                  <span className="room-live" />
                  <span className="room-name">Late night confessions 🌙</span>
                  <span className="room-cnt">34 🎙️</span>
                </div>
                <div className="room-row">
                  <span className="room-live" />
                  <span className="room-name">Cricket ke baad ki chai ☕</span>
                  <span className="room-cnt">128 🎙️</span>
                </div>
                <div className="room-row">
                  <span className="room-live" />
                  <span className="room-name">Shaayri ki raat 🌹</span>
                  <span className="room-cnt">19 🎙️</span>
                </div>
                <div className="room-row">
                  <span className="room-live" />
                  <span className="room-name">2am random baatein 🫂</span>
                  <span className="room-cnt">67 🎙️</span>
                </div>
              </div>
            </div>

            <div className="cd s4 rose reveal">
              <div className="ic">🎵</div>
              <h3>Voice notes</h3>
              <p>Raw, unfiltered voice notes — type karne ki zaroorat nahi.</p>
            </div>
            <div className="cd s4 gold reveal">
              <div className="ic">🎭</div>
              <h3>Anonymous-first</h3>
              <p>Number, face, naam — none required. Reveal only when you mean to. See how we keep every call <Link to="/safety">safe and verified</Link>.</p>
            </div>
            <div className="cd s4 teal reveal">
              <div className="ic">💪</div>
              <h3>Earn while talking</h3>
              <p>Verified vibe-partners earn coins per minute. आवाज़ = hustle. <Link to="/join-us">Become a partner</Link>.</p>
            </div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="block" id="how">
          <div className="reveal">
            <div className="kicker"><span className="kicker-line" /> By the numbers</div>
            <h2 className="h">A ticket to a <span className="hi">देसी</span> mehfil.</h2>
          </div>
          <div className="tickets reveal">
            <div className="tk"><span className="corner">★ 01</span><div className="num">1.2M+</div><div className="lbl">Active monthly users</div></div>
            <div className="tk"><span className="corner">★ 02</span><div className="num">42M</div><div className="lbl">Conversations sparked</div></div>
            <div className="tk"><span className="corner">★ 03</span><div className="num">28</div><div className="lbl">Indian states covered</div></div>
            <div className="tk"><span className="corner">★ 04</span><div className="num">8s</div><div className="lbl">Avg. match time</div></div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="block" id="cta">
          <div className="cta reveal">
            <div className="cta-grid">
              <div className="cta-l">
                <div className="kicker"><span className="kicker-line" /> Send. Receive. Vibe.</div>
                <h2>Aaj <span className="stamp">baat</span> shuru karein?</h2>
                <p className="lead">Free. Loud. India-first. Available on Android and iOS — your next favourite voice is one tap away.</p>
                <div className="app-row">
                  <a
                    className="app-btn"
                    href={playStoreUrl('home_cta')}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('play_store_click', { location: 'home_cta' })}
                  >
                    <svg width="22" height="24" viewBox="0 0 512 512" aria-hidden="true">
                      <path fill="#4285F4" d="M48,59.49v393a4.33,4.33,0,0,0,7.37,3.07L260,256,55.37,56.42A4.33,4.33,0,0,0,48,59.49Z"/>
                      <path fill="#34A853" d="M345.8,174,89.17,32.64l-.07,0c-6.81-3.64-13.36.55-13.36,7.71L260,256Z"/>
                      <path fill="#EA4335" d="M89.1,479.36c0,7.16,6.55,11.35,13.36,7.71L345.8,338,260,256Z"/>
                      <path fill="#FBBC04" d="M447.34,230.13l-82.08-45.79L260,256l105.26,71.66,82.08-45.79a20.41,20.41,0,0,0,0-51.74Z"/>
                    </svg>
                    <div><div className="top">GET IT ON</div><div className="bot">Google Play</div></div>
                  </a>
                  <a className="app-btn" href="#" style={{ background: '#fff', color: 'var(--ink)' }}>
                    <svg width="17" height="20" viewBox="0 0 814 1000" fill="var(--ink)" aria-hidden="true">
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-194.3 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                    </svg>
                    <div>
                      <div className="top" style={{ color: 'var(--ink-mute)' }}>DOWNLOAD ON</div>
                      <div className="bot">App Store</div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="cta-r">
                <div className="stamp-circle">BHARAT<br />POSTAL<br />2026</div>
                <div style={{ fontFamily: "'Caveat',cursive", fontSize: '28px', color: 'var(--gold)', transform: 'rotate(-3deg)', position: 'relative', zIndex: 1 }}>scan &amp; vibe</div>
                <div className="qr" style={{ position: 'relative', zIndex: 1 }}>
                  <img src="/playstore-qr.png" alt="Scan to get Baatcheet on Google Play" width="400" height="400" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.6)', fontFamily: "'DM Mono',monospace", letterSpacing: '.1em', position: 'relative', zIndex: 1 }}>point camera here →</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Home;
